/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    createContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useContext,
} from 'react';
import * as logger from 'common/utils/Logger';
// eslint-disable-next-line import/no-unresolved
import { useRegisterSW } from 'virtual:pwa-register/react';

export const ServiceWorkerContext = createContext({});

const timeoutFallbackDuration = 10000; // 10 seconds

export const ServiceWorkerProvider = ({ children }) => {
    const [pathname, setPathname] = useState('');
    const [registration, setRegistration] = useState(undefined);

    const {
        needRefresh: [needRefresh, setNeedRefresh],
        offlineReady: [offlineReady, setOfflineReady],
        updateServiceWorker: updateSW,
    } = useRegisterSW({
        immediate: true,
        onRegisteredSW(swUrl, registration) {
            // Only use the registration if in production and browser supports SW
            if (import.meta.env.MODE !== 'production') {
                logger.info(
                    '[ServiceWorkerProvider] Not in production mode, skipping SW registration'
                );
                return;
            }

            if (!('serviceWorker' in navigator)) {
                logger.info(
                    '[ServiceWorkerProvider] Service workers not supported in this browser'
                );
                return;
            }

            logger.log(`[ServiceWorkerProvider] Service Worker at: ${swUrl}`);

            if (registration) {
                setRegistration(registration);
            }
        },
        onRegisterError(error) {
            logger.error(
                '[ServiceWorkerProvider] Service worker registration error:',
                error,
                { skipSentry: true }
            );
        },
    });

    useEffect(() => {
        if (registration && pathname) {
            logger.log(
                '[ServiceWorkerProvider] Route changed, checking for updates'
            );
            registration.update().catch(err => {
                logger.error(
                    '[ServiceWorkerProvider] Error checking for updates on route change:',
                    err,
                    { skipSentry: true }
                );
            });
        }
    }, [registration, pathname]);

    // Listen for Vite preload errors (chunk load failures) and trigger update modal
    // This handles cases where code chunks fail to load due to deployment updates
    // Note: https://vite.dev/guide/build#load-error-handling
    useEffect(() => {
        const handleVitePreloadError = () => {
            logger.log(
                '[ServiceWorkerProvider] Vite preload error detected, showing update modal'
            );
            setNeedRefresh(true);
        };

        globalThis.addEventListener(
            'vite:preloadError',
            handleVitePreloadError
        );

        return () => {
            globalThis.removeEventListener(
                'vite:preloadError',
                handleVitePreloadError
            );
        };
    }, [setNeedRefresh]);

    // Multi-tab reload: Listen for SW activation to reload all tabs
    // When a SW is activated (by any tab), all tabs should reload to use the new version
    useEffect(() => {
        const waitingServiceWorker = registration?.waiting;

        if (waitingServiceWorker) {
            const handleStateChange = event => {
                if (event.target.state === 'activated') {
                    logger.log(
                        '[ServiceWorkerProvider] Service worker activated, reloading page'
                    );
                    globalThis.location.reload();
                }
            };

            waitingServiceWorker.addEventListener(
                'statechange',
                handleStateChange
            );

            return () => {
                waitingServiceWorker.removeEventListener(
                    'statechange',
                    handleStateChange
                );
            };
        }
    }, [registration]);

    const updateAssets = useCallback(() => {
        logger.log(
            '[ServiceWorkerProvider] User clicked update, triggering service worker update'
        );

        const fallbackTimeout = setTimeout(() => {
            logger.log(
                '[ServiceWorkerProvider] Update timed out, forcing page reload'
            );
            globalThis.location.reload();
        }, timeoutFallbackDuration);

        return Promise.resolve(updateSW(true))
            .then(() => {
                clearTimeout(fallbackTimeout);
            })
            .catch(err => {
                clearTimeout(fallbackTimeout);
                logger.error(
                    '[ServiceWorkerProvider] Error updating service worker:',
                    err,
                    { skipSentry: true }
                );
                globalThis.location.reload();
            });
    }, [updateSW]);

    // Manual update method with autoUpdate option
    const updateServiceWorkerManual = useCallback(
        autoUpdate => {
            logger.log(
                '[ServiceWorkerProvider] Manual update requested, autoUpdate (no modal):',
                autoUpdate
            );

            // Check for updates if registration exists
            if (registration) {
                return registration.update().catch(err => {
                    logger.error(
                        '[ServiceWorkerProvider] Error checking for updates:',
                        err,
                        { skipSentry: true }
                    );
                });
            }

            if (autoUpdate) {
                // If autoUpdate is true, trigger the update immediately without showing modal
                logger.log(
                    '[ServiceWorkerProvider] autoUpdate is true, updating service worker immediately. no modal should show'
                );

                return updateAssets();
            }

            return Promise.resolve();
        },
        [registration, updateAssets]
    );

    const value = useMemo(
        () => ({
            isUpdateAvailable: needRefresh,
            offlineReady,
            updateAssets,
            updateServiceWorker: updateServiceWorkerManual,
            registration,
            setPathname,
        }),
        [
            needRefresh,
            offlineReady,
            updateAssets,
            updateServiceWorkerManual,
            registration,
        ]
    );

    return (
        <ServiceWorkerContext.Provider value={value}>
            {children}
        </ServiceWorkerContext.Provider>
    );
};

// With this React Hook we'll be able to access `isUpdateAvailable` and `updateAssets`
export const useServiceWorker = () => {
    return useContext(ServiceWorkerContext);
};
